import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Select, Input, DatePicker, Button, message, Modal, Form, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AttachmentUpload from '../components/AttachmentUpload';
import api from '../utils/axios';

const { Option } = Select;

const statusColor = (status) => {
  switch (status) {
    case 'completed': return 'green';
    case 'in_progress': return 'blue';
    case 'pending':
    default: return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'completed': return '已完成';
    case 'in_progress': return '进行中';
    case 'pending': return '待开始';
    default: return '待开始';
  }
};

// 超期任务：未完成且截止日期早于今天（当天截止不算超期）
const isOverdueTask = (record) => {
  if (!record || !record.due_date) return false;
  if (record.status === 'completed' || record.status === 'cancelled') return false;
  const due = new Date(record.due_date);
  if (isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
};

const EmployeesTasksAll = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminUser = user?.role === 'super_admin' || user?.role === 'hr_manager';
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  
  // 完成任务相关状态
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [completeForm] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [filters, setFilters] = useState({
    status: 'incomplete',
    section: undefined,
    search: undefined,
    sort_by: 'due_date',
    sort_dir: 'asc',
  });

  const loadData = async (override = {}) => {
    setLoading(true);
    try {
      const params = { page, limit, ...filters, ...override };
      const res = await api.get('/employee-tasks/tasks', { params });
      console.log('任务列表API响应:', res.data);
      if (res.data?.success) {
        console.log('任务数据:', res.data.data.tasks);
        setTasks(res.data.data.tasks || []);
        setTotal(res.data.data.total || 0);
      }
    } catch (e) {
      console.error('加载任务列表失败:', e);
      message.error('加载任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, limit, filters]);

  // 一键关闭离职/辞退员工的未完成任务（仅管理员）
  const handleCloseResigned = () => {
    Modal.confirm({
      title: '一键清除离职员工任务',
      content: '将把所有已离职/辞退员工的未完成任务批量关闭（状态置为已取消，备注「员工已离职，任务中断」），关闭后任务列表不再显示。是否继续？',
      okText: '确认关闭',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await api.post('/employee-tasks/tasks/close-resigned');
          const closed = res.data?.closed ?? 0;
          if (closed > 0) {
            message.success(`已关闭 ${closed} 个离职员工的未完成任务`);
          } else {
            message.info('没有需要关闭的离职员工任务');
          }
          loadData();
        } catch (e) {
          console.error('一键关闭离职员工任务失败:', e);
          message.error(e.response?.data?.message || '操作失败，请稍后重试');
          throw e;
        }
      }
    });
  };

  const handleComplete = (record) => {
    setCurrentTask(record);
    setCompleteModalVisible(true);
    completeForm.resetFields();
    setAttachments([]);
  };

  const handleCompleteSubmit = async (values, action) => {
    if (!currentTask) {
      console.error('当前任务为空');
      message.error('任务信息错误，请刷新页面重试');
      return;
    }
    
    setSubmitting(true);
    try {
      const requestData = {
        status: action === 'save' ? 'in_progress' : 'completed',
        completion_notes: values.completion_notes || ''
      };
      
      if (action === 'completed') {
        // 转换为MySQL兼容的日期时间格式
        const now = new Date();
        requestData.completed_at = now.getFullYear() + '-' + 
          String(now.getMonth() + 1).padStart(2, '0') + '-' + 
          String(now.getDate()).padStart(2, '0') + ' ' + 
          String(now.getHours()).padStart(2, '0') + ':' + 
          String(now.getMinutes()).padStart(2, '0') + ':' + 
          String(now.getSeconds()).padStart(2, '0');
      }
      
      // 添加附件ID
      if (attachments && attachments.length > 0) {
        const attachmentIds = attachments.map(file => file.fileData?.id || file.uid).filter(Boolean);
        requestData.attachment_ids = attachmentIds;
      }

      console.log('提交任务数据:', {
        taskId: currentTask.id,
        employeeId: currentTask.employee_id,
        action,
        requestData
      });

      await api.patch(`/employee-tasks/employees/${currentTask.employee_id}/tasks/${currentTask.id}`, requestData);
      
      message.success(action === 'save' ? '任务已暂存' : '任务已完成');
      setCompleteModalVisible(false);
      setCurrentTask(null);
      loadData();
    } catch (e) {
      console.error('任务操作失败:', e);
      console.error('错误详情:', e.response?.data);
      
      // 更详细的错误处理
      let errorMessage = action === 'save' ? '暂存任务失败' : '完成任务失败';
      
      if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.message) {
        errorMessage = e.message;
      } else if (typeof e === 'string') {
        errorMessage = e;
      } else {
        errorMessage = '操作失败，请稍后重试';
      }
      
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteCancel = () => {
    setCompleteModalVisible(false);
    setCurrentTask(null);
    completeForm.resetFields();
    setAttachments([]);
  };

  const columns = [
    { 
      title: '任务名称', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200, 
      ellipsis: true,
      render: (name, record) => {
        const tooltipContent = (
          <div>
            {record.description && (
              <div style={{ marginBottom: 8 }}>
                <strong>任务描述：</strong><br />
                {record.description}
              </div>
            )}
            {record.created_at && (
              <div style={{ marginBottom: 4, fontSize: '12px', color: '#999' }}>
                <strong>创建时间：</strong>{new Date(record.created_at).toLocaleString('zh-CN', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
            {(record.created_by_name || record.created_by_username) && (
              <div style={{ fontSize: '12px', color: '#999' }}>
                <strong>创建人：</strong>{record.created_by_name || record.created_by_username || '未知'}
              </div>
            )}
          </div>
        );

        return (
          record.description || record.created_at || record.created_by_name ? (
            <Tooltip title={tooltipContent} placement="topLeft">
              <span>{name}</span>
            </Tooltip>
          ) : (
            <span>{name}</span>
          )
        );
      }
    },
    { title: '员工', dataIndex: 'employee_name', key: 'employee_name', width: 180, render: (text, record) => (
      <div>
        <a 
          onClick={() => {
            // 优先使用员工工号搜索，如果没有工号则使用姓名
            const searchTerm = record.employee_no || record.employee_name;
            if (searchTerm) {
              navigate(`/employees?search=${encodeURIComponent(searchTerm)}`);
            } else {
              navigate('/employees');
            }
          }}
          style={{ color: '#1890ff', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {record.employee_name || '未知员工'}
        </a>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
          工号：{record.employee_no || '-'}
        </div>
      </div>
    ) },
    { title: '部门', dataIndex: 'department_name', key: 'department_name', width: 140 },
    { title: '岗位', dataIndex: 'position_name', key: 'position_name', width: 140 },
    { title: '截止', dataIndex: 'due_date', key: 'due_date', width: 140 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s) => <Tag color={statusColor(s)}>{getStatusText(s)}</Tag> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180 },
    {
      title: '操作', 
      key: 'actions', 
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.status !== 'completed' && (
            <Button size="small" type="link" onClick={() => handleComplete(record)}>完成</Button>
          )}
          {(record.description || record.created_at || record.created_by_name) && (
            <Tooltip title={
              <div>
                {record.description && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>任务描述：</strong><br />
                    {record.description}
                  </div>
                )}
                {record.created_at && (
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#999' }}>
                    <strong>创建时间：</strong>{new Date(record.created_at).toLocaleString('zh-CN', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </div>
                )}
                {(record.created_by_name || record.created_by_username) && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    <strong>创建人：</strong>{record.created_by_name || record.created_by_username || '未知'}
                  </div>
                )}
              </div>
            } placement="topLeft">
              <Button size="small" type="link">详情</Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>
      <Card title="未完成任务列表" size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>状态</span>
          <Select value={filters.status} onChange={(v) => setFilters(prev => ({ ...prev, status: v }))} style={{ width: 140 }}>
            <Option value="incomplete">未完成</Option>
            <Option value="pending">待开始</Option>
            <Option value="in_progress">进行中</Option>
            <Option value="completed">已完成</Option>
          </Select>


          <span>搜索</span>
          <Input placeholder="任务/员工姓名/工号" allowClear style={{ width: 220 }} value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value || undefined }))} />

          <span>排序</span>
          <Select value={filters.sort_by} onChange={(v) => setFilters(prev => ({ ...prev, sort_by: v }))} style={{ width: 140 }}>
            <Option value="due_date">截止日期</Option>
            <Option value="created_at">创建时间</Option>
          </Select>
          <Select value={filters.sort_dir} onChange={(v) => setFilters(prev => ({ ...prev, sort_dir: v }))} style={{ width: 120 }}>
            <Option value="asc">升序</Option>
            <Option value="desc">降序</Option>
          </Select>

          <Button onClick={() => loadData()} type="primary">查询</Button>

          {isAdminUser && (
            <Tooltip title="将所有已离职/辞退员工的未完成任务批量关闭">
              <Button danger onClick={handleCloseResigned}>一键清除离职员工任务</Button>
            </Tooltip>
          )}
        </Space>
      </Card>

      <Card>
        {/* 超期任务整行淡红背景 */}
        <style>{`
          .overdue-task-row > td { background: #fff1f0 !important; }
          .overdue-task-row:hover > td { background: #ffd9d2 !important; }
        `}</style>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={tasks}
          columns={columns}
          rowClassName={(record) => (isOverdueTask(record) ? 'overdue-task-row' : '')}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setLimit(ps);
            }
          }}
          size="middle"
        />
      </Card>

      {/* 完成任务模态框 */}
      <Modal
        title={`完成任务 - ${currentTask?.name || ''}`}
        open={completeModalVisible}
        onCancel={handleCompleteCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleCompleteCancel}>
            取消
          </Button>,
          <Button 
            key="save" 
            type="default" 
            loading={submitting}
            onClick={() => {
              completeForm.validateFields()
                .then(values => {
                  handleCompleteSubmit(values, 'save');
                })
                .catch(errorInfo => {
                  console.error('表单验证失败:', errorInfo);
                  message.error('请检查表单填写是否正确');
                });
            }}
          >
            保存
          </Button>,
          <Button 
            key="complete" 
            type="primary" 
            loading={submitting}
            onClick={() => {
              completeForm.validateFields()
                .then(values => {
                  handleCompleteSubmit(values, 'completed');
                })
                .catch(errorInfo => {
                  console.error('表单验证失败:', errorInfo);
                  message.error('请检查表单填写是否正确');
                });
            }}
          >
            完成
          </Button>,
        ]}
      >
        <Form
          form={completeForm}
          layout="vertical"
          onFinish={(values) => {
            // 默认保存操作
            handleCompleteSubmit(values, 'save');
          }}
        >
          <Form.Item
            name="completion_notes"
            label="完成情况描述"
            rules={[{ required: true, message: '请填写完成情况描述' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="请详细描述任务的完成情况..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="attachments"
            label="相关附件"
            extra="支持图片、PDF、Word、Excel等格式，单个文件不超过10MB"
          >
            <AttachmentUpload
              value={attachments}
              onChange={setAttachments}
              employeeId={currentTask?.employee_id}
              compact={false}
              showQrButton={true}
              hideList={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </main>
  );
};

export default EmployeesTasksAll;


